import axios from "axios";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  ExperienceActionRequestBodyType,
  ExperienceActionRequestPathParamsType,
  ExperienceActionResponseBodyType,
} from "@intelligems/ig-types/functions/experiences/experience-action";
import { ExperienceCategory } from "@intelligems/ig-types/dtos/experience-categories";
import { TabEnumFe } from "@intelligems/ig-types/fe-only/experiences";
import { officialCategoryName } from "@intelligems/ig-utils/common";
import { maybeOpenEarlyTestReport } from "@src/features/experiments/components/earlyTestGuard/maybeOpenEarlyTestReport";
import { getMutationErrorMessage } from "@src/queryClient";
import { EXPERIMENT_ROUTES } from "@src/routes/routes";
import {
  initExperimentFromDto,
  setExperienceTab,
  setExperienceViewMode,
} from "@src/store/slices/experiences";
import { setIgnoreUpdateDetected } from "@src/store/slices/global";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useConfiguration } from "../configurations/use-configuration";
import {
  experienceKeys,
  historyKeys,
  invalidateModuleExperienceListQueries,
} from "../queryKeys";
import { ApiRequest } from "../types/api";

import { experienceAction } from "./experience-action";

export const useEndExperience = ({
  experienceId,
  category,
}: {
  experienceId?: string;
  category: ExperienceCategory;
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const shouldDuplicateProducts = useConfiguration<boolean>(
    "should_duplicate_products"
  );
  const dispatch = useDispatch();
  return useMutation<
    ExperienceActionResponseBodyType,
    Error,
    ApiRequest<
      ExperienceActionRequestBodyType,
      ExperienceActionRequestPathParamsType & {
        name: string;
      }
    >
  >({
    mutationKey: ["endExperience"],
    onMutate: () => {
      dispatch(setIgnoreUpdateDetected(true));
    },
    mutationFn: async (data) => experienceAction(data),
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 503) {
          toast.info(`Ending ${officialCategoryName(category, true)}...`);
        } else {
          const errorDetail = getMutationErrorMessage(error);
          toast.error(
            `Error ending ${officialCategoryName(category, true)} ${errorDetail ? `: ${errorDetail}` : ""}`
          );
        }
      }
    },
    onSuccess: async (data, payload) => {
      await queryClient.invalidateQueries({
        queryKey: [{ scope: `experiences-list-${data?.experience?.category}` }],
      });
      invalidateModuleExperienceListQueries(queryClient);

      if (experienceId) {
        // invalidating instead of setting bc return is experience array
        await queryClient.invalidateQueries({
          queryKey: experienceKeys.one(experienceId),
        });
        void queryClient.invalidateQueries({
          queryKey: historyKeys.one(experienceId),
        });
      }
      if (data.experience?.category === "experiment") {
        dispatch(
          initExperimentFromDto({
            experience: data.experience,
            testFlowType: data.experience.type,
            tab: TabEnumFe.ANALYTICS,
          })
        );
        dispatch(setExperienceTab(TabEnumFe.ANALYTICS));
        dispatch(setExperienceViewMode("view"));
      }

      if (
        shouldDuplicateProducts?.value &&
        data.experience?.testTypes?.hasTestPricing &&
        data.experience.category === "experiment"
      ) {
        void navigate(
          `${EXPERIMENT_ROUTES.Experiment}/${payload.pathParams.experienceId}?tab=${TabEnumFe.ANALYTICS}`,
          {
            state: {
              showDuplicateProducts: true,
              showStartTestRow: true,
              tab: TabEnumFe.ANALYTICS,
            },
          }
        );
      }

      maybeOpenEarlyTestReport(dispatch, data.experience, "end");
    },
  });
};
